import { optInPostHog, optOutPostHog } from '@/lib/analytics/posthog-init';

/**
 * Where a stored consent record came from (REK-3389).
 *
 * - `explicit` (or absent -- legacy rows): the user chose on the banner / preferences
 *   modal / privacy settings. Never overridden by implied or account-restored consent.
 * - `implied`: written by `grantImpliedConsent` on an authentication action
 *   (analytics only). Replaceable by the user's saved account decision.
 * - `account`: restored from `user_consents` after login. Re-restorable.
 */
export type ConsentRecordSource = 'explicit' | 'implied' | 'account';

export interface CookieConsent {
  essential: boolean; // Always true, cannot be disabled
  analytics: boolean;
  personalization: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
  source?: ConsentRecordSource;
}

const COOKIE_CONSENT_KEY = 'reklaim_cookie_consent';
const CONSENT_VERSION = '1.0';

export const CONSENT_UPDATED_EVENT = 'reklaim-consent-updated';

/**
 * Notify `useCookieConsent` (banner state) that consent changed outside the banner.
 *
 * Contract (REK-3389): dispatchers have ALREADY applied the change to GTM Consent
 * Mode and PostHog (via `applyConsentToVendors`). The listener only syncs React
 * state and hides the banner -- it must not re-send a GTM consent update, or one
 * change would produce two Consent Mode updates.
 */
export function dispatchConsentUpdated(): void {
  window.dispatchEvent(new Event(CONSENT_UPDATED_EVENT));
}

export const getDefaultConsent = (): CookieConsent => ({
  essential: true,
  analytics: false,
  personalization: false,
  marketing: false,
  timestamp: new Date().toISOString(),
  version: CONSENT_VERSION,
});

export const getStoredConsent = (): CookieConsent | null => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;
    
    const consent = JSON.parse(stored) as CookieConsent;
    
    // Check if consent version matches current version
    if (consent.version !== CONSENT_VERSION) {
      // Version mismatch - require re-consent
      return null;
    }
    
    return consent;
  } catch {
    return null;
  }
};

export const saveConsent = (consent: CookieConsent): void => {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      ...consent,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    }));
  } catch (error) {
    console.error('Failed to save cookie consent:', error);
  }
};

export const hasConsented = (): boolean => {
  return getStoredConsent() !== null;
};

export const hasAnalyticsConsent = (): boolean => {
  const consent = getStoredConsent();
  return consent?.analytics ?? false;
};

export const hasPersonalizationConsent = (): boolean => {
  const consent = getStoredConsent();
  return consent?.personalization ?? false;
};

export const hasMarketingConsent = (): boolean => {
  const consent = getStoredConsent();
  return consent?.marketing ?? false;
};

export const acceptAllCookies = (): CookieConsent => {
  const consent: CookieConsent = {
    essential: true,
    analytics: true,
    personalization: true,
    marketing: true,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  saveConsent(consent);
  return consent;
};

export const rejectNonEssentialCookies = (): CookieConsent => {
  const consent: CookieConsent = {
    essential: true,
    analytics: false,
    personalization: false,
    marketing: false,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  saveConsent(consent);
  return consent;
};

export const updateConsent = (updates: Partial<Omit<CookieConsent, 'essential' | 'timestamp' | 'version'>>): CookieConsent => {
  const current = getStoredConsent() || getDefaultConsent();
  const consent: CookieConsent = {
    ...current,
    ...updates,
    essential: true, // Essential cookies cannot be disabled
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
    // A deliberate change is an explicit decision (REK-3389).
    source: 'explicit',
  };
  saveConsent(consent);
  return consent;
};

export const clearConsent = (): void => {
  try {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
  } catch (error) {
    console.error('Failed to clear cookie consent:', error);
  }
};

export type ImpliedConsentSource =
  | 'magic_link_verification'
  | 'otp_login'
  // REK-1591: clicking a Braze one-tap login link is an affirmative opt-in
  // on par with magic-link / OTP, so the one-tap interstitial grants implied
  // consent before navigating to the dashboard.
  | 'one_tap_login'
  // REK-1730: completing the one-click sign-up flow (ticking the combined
  // Terms+Privacy checkbox and confirming) is an explicit affirmative opt-in,
  // so the /auth/signup page grants implied consent before navigating to
  // /onboarding.
  | 'one_click_signup'
  // REK-2186: completing a social OAuth sign-UP from a consumer surface
  // (the user affirmed 18+/ToS before the provider redirect) is the same
  // affirmative opt-in as the OTP signup paths. Granted by /auth/sso-callback
  // BEFORE the session activates. Provider-agnostic (replaces google_sso_signup).
  | 'social_sso_signup';

/**
 * Map a consent record to Google Consent Mode v2 grants. Mirrors
 * `useCookieConsent`'s `syncConsentToGTM` and `gtm.applyStoredConsentBeforeGtmLoad`.
 * `ad_*` signals follow the marketing flag ONLY (REK-3389).
 */
export const consentToGtmGrants = (consent: CookieConsent) => ({
  analytics_storage: consent.analytics ? 'granted' : 'denied',
  ad_storage: consent.marketing ? 'granted' : 'denied',
  ad_user_data: consent.marketing ? 'granted' : 'denied',
  ad_personalization: consent.marketing ? 'granted' : 'denied',
  functionality_storage: 'granted',
  personalization_storage: consent.personalization ? 'granted' : 'denied',
});

/**
 * Push a consent record to GTM Consent Mode and PostHog. Exactly ONE
 * `gtag('consent','update')` per call.
 *
 * GTM is updated inline rather than importing `@/lib/analytics`'s
 * `updateConsent` -- the analytics barrel re-exports from `./gtm`, and
 * `gtm.ts` already imports `getStoredConsent` from this file, which would
 * form a circular dependency cookieConsent -> analytics -> gtm ->
 * cookieConsent (Copilot R1, REK-1489). The wire format is identical to
 * `gtm.updateConsent`'s body.
 *
 * PostHog (REK-3389): analytics=true initializes the SDK (deferred until
 * now) and opts in; analytics=false opts out only if it was ever initialized.
 */
export const applyConsentToVendors = (consent: CookieConsent): void => {
  if (typeof window !== 'undefined') {
    window.gtag?.('consent', 'update', consentToGtmGrants(consent));
  }
  if (consent.analytics) {
    optInPostHog();
  } else {
    optOutPostHog();
  }
};

/**
 * REK-3389: write + apply "analytics only" consent -- essential + analytics
 * on; personalization and marketing OFF; GTM `ad_storage` / `ad_user_data` /
 * `ad_personalization` stay DENIED. This is what an authentication action
 * implies (see `grantImpliedConsent`); marketing needs explicit opt-in.
 */
export const applyAnalyticsOnlyConsent = (): CookieConsent => {
  const consent: CookieConsent = {
    essential: true,
    analytics: true,
    personalization: false,
    marketing: false,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
    source: 'implied',
  };
  saveConsent(consent);
  applyConsentToVendors(consent);
  dispatchConsentUpdated();
  return consent;
};

/**
 * REK-3389: write + apply analytics AND marketing consent from the explicit,
 * optional "Allow analytics and marketing cookies" signup checkbox. Unlike
 * `grantImpliedConsent` this intentionally overrides a stored decision: the
 * user just ticked an unticked-by-default box. Personalization is preserved.
 */
export const applyAnalyticsAndMarketingConsent = (source: ImpliedConsentSource): CookieConsent => {
  const consent: CookieConsent = {
    essential: true,
    analytics: true,
    personalization: getStoredConsent()?.personalization ?? false,
    marketing: true,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
    source: 'explicit',
  };
  saveConsent(consent);
  applyConsentToVendors(consent);
  dispatchConsentUpdated();
  try {
    console.log(JSON.stringify({ event: 'consent_marketing_opt_in', source }));
  } catch {
    // ignore JSON.stringify failures (defensive; source is a string literal)
  }
  return consent;
};

/**
 * Grant cookie consent on behalf of the user when they take an affirmative
 * authentication action -- e.g. completing a magic-link verification on
 * `/auth/confirm`, or supplying an OTP code on `/auth`.
 *
 * REK-3389 (Cookie Policy alignment): an authentication action implies
 * consent to ANALYTICS cookies only. Marketing / advertising cookies
 * (`ad_storage`, `ad_user_data`, `ad_personalization`) and personalization
 * need an explicit opt-in (banner Accept, or the signup checkbox), so they
 * stay denied here. This replaces the earlier accept-all behaviour (REK-1489).
 *
 * Respects any prior decision: if a choice is already stored (accept, reject,
 * or partial), this is a no-op and returns `false`. Returns `true` iff a new
 * analytics-only consent record was written.
 *
 * REK-1489: the cookie banner should not appear, and PostHog analytics
 * should not be silently dropped, for users who arrive on `/dashboard` via a
 * magic link we sent in response to their own affirmative-opt-in (Meta Lead
 * Form, email click). Mirrors the inline closure that previously lived in
 * `useFastAuth.applyImpliedConsent`.
 */
export const grantImpliedConsent = (source: ImpliedConsentSource): boolean => {
  if (hasConsented()) return false;
  applyAnalyticsOnlyConsent();
  try {
    console.log(JSON.stringify({ event: 'consent_implied_granted', source }));
  } catch {
    // ignore JSON.stringify failures (defensive; source is a string literal)
  }
  return true;
};

/** Minimal `user_consents` row shape needed to restore cookie consent. */
export interface AccountConsentRow {
  consent_type: string;
  granted: boolean;
}

/**
 * REK-3389: restore the cookie decision from the signed-in user's
 * `user_consents` rows (`analytics_tracking`, `third_party_cookies`) into the local
 * CookieConsent record, then apply it to GTM / PostHog and hide the banner.
 *
 * Marketing alias: signup writes `marketing`, while `TrackingPreferencesCard`
 * historically wrote `third_party_cookies` for the same marketing/ads
 * preference (it now dual-writes both). The `marketing` row wins when present;
 * otherwise `third_party_cookies` is used with identical `granted` semantics.
 *
 * - No relevant rows -> nothing to restore, returns `false`.
 * - An EXPLICIT local decision (banner / preferences / privacy settings)
 *   wins over the account and is left untouched, returns `false`.
 * - Otherwise (no local record, or only an implied/account-sourced one) the
 *   account decision is written:
 *   - Analytics: an explicit `analytics_tracking` row wins (including
 *     `granted: false`). A MISSING row is not a denial (the privacy card only
 *     writes it when changed), so the existing local analytics value is
 *     preserved (e.g. login-implied analytics=true); with no local record it
 *     defaults to false.
 *   - Marketing: a missing marketing/third_party_cookies row means "not granted".
 *   - Personalization is preserved from any existing local record, else false.
 *
 * Callers pass rows read with the same query as `TrackingPreferencesCard`
 * (`user_consents` where `revoked_at IS NULL`). Returns `true` iff the local
 * record changed.
 */
export const applyAccountConsents = (rows: ReadonlyArray<AccountConsentRow>): boolean => {
  const analyticsRow = rows.find((r) => r.consent_type === 'analytics_tracking');
  const marketingRow =
    rows.find((r) => r.consent_type === 'marketing') ??
    rows.find((r) => r.consent_type === 'third_party_cookies');
  if (!analyticsRow && !marketingRow) return false;

  const existing = getStoredConsent();
  if (existing && existing.source !== 'implied' && existing.source !== 'account') {
    return false;
  }

  const analytics = analyticsRow ? analyticsRow.granted : (existing?.analytics ?? false);
  const marketing = marketingRow?.granted ?? false;
  if (
    existing &&
    existing.source === 'account' &&
    existing.analytics === analytics &&
    existing.marketing === marketing
  ) {
    return false;
  }

  const consent: CookieConsent = {
    essential: true,
    analytics,
    personalization: existing?.personalization ?? false,
    marketing,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
    source: 'account',
  };
  saveConsent(consent);
  applyConsentToVendors(consent);
  dispatchConsentUpdated();
  return true;
};

/** True when a stored decision exists that the account must not override. */
export const hasExplicitLocalConsent = (): boolean => {
  const stored = getStoredConsent();
  return stored !== null && stored.source !== 'implied' && stored.source !== 'account';
};
